import React, { useState, useEffect } from 'react';
import { Card, Spin, Alert, Tag, Row, Col, Empty, Icon, Tooltip } from 'antd';
import { getInvoicePayments } from '../../api/Billing';
import moment from 'moment';
import Copyable from '../common/Copyable';

const InvoicePayments = ({ accountInvoiceId, activeTab }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    if (activeTab === "payments" && accountInvoiceId) {
      fetchInvoicePayments();
    }
  }, [activeTab, accountInvoiceId]);


  const fetchInvoicePayments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getInvoicePayments(accountInvoiceId);
      
      if (response.success) {
        setInvoiceData(response.invoice_details);
      } else {
        setError('Failed to fetch invoice details');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching invoice payments');
    } finally {
      setLoading(false);
    }
  };

const getPaymentStatusTag = (status) => {
  const statusConfig = {
    'fully paid': {
      color: '#228a50',
      text: 'Fully Paid',
      icon: 'check-circle'
    },
    'unpaid': {
      color: '#ed5857',
      text: 'Unpaid',
      icon: 'exclamation-circle'
    },
    'partial paid': {
      color: '#ec8108',
      text: 'Partial Paid',
      icon: 'clock-circle'
    },
    'over paid': {
      color: '#395484',
      text: 'Over Paid',
      icon: 'rise'
    }
  };

  const config = statusConfig[status] || {
    color: 'default',
    text: status,
    icon: null
  };

  return (
    <Tag
      color={config.color}
      style={{
        borderRadius: '12px',
        padding: '2px 12px',
        border: 'none',
        color: 'white',
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center'
      }}
    >
      {config.icon && (
        <Icon
          type={config.icon}
          style={{ marginRight: 6 }}
        />
      )}
      {config.text}
    </Tag>
  );
};


  const formatDate = (dateString) => {
    return moment.utc(dateString).format('MMM DD, YYYY');
  };

  const getPaymentModeLabel = (payMode) => {
    const modeLabels = {
      'ACH': 'ACH',
      'CREDIT_NOTE': 'Credit Note',
      'CHECK': 'Check',
    };
    return modeLabels[payMode] || payMode;
  };

  const renderTransactionHistory = (transactions) => {
    if (!transactions || transactions.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>No payments recorded</div>
              <div style={{ color: '#bfbfbf', fontSize: '12px' }}>Payments will appear here once initiated</div>
            </div>
          }
          style={{ padding: '40px 0' }}
        />
      );
    }

    return (
      <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
        {transactions.map((transaction, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 0',
              borderBottom: idx < transactions.length - 1 ? '1px solid #f0f0f0' : 'none'
            }}
          >
            {/* Left Section */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flex: 1,
              minWidth: 0
            }}>
              {/* Status Icon */}
              <Icon
                type={transaction.is_revert ? 'close-circle' : 'check-circle'}
                style={{
                  color: transaction.is_revert ? '#ed5857' : '#228a50',
                  fontSize: '18px',
                  flexShrink: 0
                }}
              />

              {/* Date */}
              <span style={{
                width: '110px',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                fontSize: '14px',
                color: '#595959'
              }}>
                {formatDate(transaction.check_payment_date)}
              </span>

              {/* Payment Mode */}
              <span style={{
                width: '350px',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                fontSize: '14px',
                fontWeight: 500,
                color: '#262626',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {getPaymentModeLabel(transaction.pay_mode)} #{transaction.check_ach_no}
              </span>

              {/* Order Number */}
              <span style={{
                width: '120px',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                fontSize: '14px',
                color: '#595959'
              }}>
                {transaction.order_number || '--'}
              </span>

            </div>


            {/* Amount */}
            <div
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: transaction.is_revert
                  ? '#ed5857'            // red for reverted / negative
                  : transaction.order_received_amount > 0
                  ? '#2e7d32'            // green for positive
                        : '#262626',     // default
                marginLeft: '24px',
                paddingRight: '8px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {transaction.is_revert
                ? '-'
                : transaction.order_received_amount > 0
                ? '+'
                : ''}
              ${Math.abs(transaction.order_received_amount).toFixed(2)}
            </div>

          </div>
        ))}
      </div>

    );
  };

  const hasInvoiceData = (invoiceDetails) => {
    return invoiceDetails && 
           Object.keys(invoiceDetails).length > 0 && 
           invoiceDetails.invoice_number;
  };

  const formatLinkedOrder = (order) => {
    const parts = [];
    
    // Parent HAWB and Order
    if (order.parent_hawb) {
      parts.push(
        <span key="parent-hawb" style={{ fontWeight: 500 }}>{order.parent_hawb}</span>
      );
      if (order.parent_order_number) {
        parts.push(
          <span key="parent-sep" style={{ margin: '0 4px', color: '#8c8c8c' }}>-</span>,
          <span key="parent-order">{order.parent_order_number}</span>
        );
      }
    } else if (order.parent_order_number) {
      // Only order number, no HAWB
      parts.push(<span key="parent-order">{order.parent_order_number}</span>);
    }

    if (parts.length > 0 && (order.debit_hawb || order.debit_order_number)) {
      parts.push(
        <span key="arrow" style={{ margin: '0 6px', color: '#722ed1', fontWeight: 600 }}>→</span>
      );
    }

    // Debit HAWB and Order
    if (order.debit_hawb) {
      parts.push(
        <span key="debit-hawb" style={{ fontWeight: 500 }}>{order.debit_hawb}</span>
      );
      if (order.debit_order_number) {
        parts.push(
          <span key="debit-sep" style={{ margin: '0 4px', color: '#8c8c8c' }}>-</span>,
          <span key="debit-order">{order.debit_order_number}</span>
        );
      }
    } else if (order.debit_order_number) {
      // Only order number, no HAWB
      parts.push(<span key="debit-order">{order.debit_order_number}</span>);
    }

    return parts;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Loading invoice details..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  if (!invoiceData) {
    return (
      <Alert
        message="No Data"
        description="No invoice details available"
        type="info"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  const { parent_invoice_details, debit_invoice_details, linked_orders } = invoiceData;
  
  const hasParentInvoice = hasInvoiceData(parent_invoice_details);
  const hasDebitInvoice = hasInvoiceData(debit_invoice_details);
  const hasLinkedOrders = linked_orders && linked_orders.length > 0;

  return (
    <div style={{ padding: '0' }} data-testid="payments-tab">
      {/* Linked Parent Invoice Section */}
      {hasParentInvoice && (
        <Card 
          style={{ 
            marginBottom: '20px',
            backgroundColor: '#f6f3ff',
            border: '1px solid #e8e3f5'
          }}
          bodyStyle={{ padding: '20px' }}
        >
          {hasLinkedOrders && <div style={{ marginBottom: '16px' }}>
            <Icon type="link" style={{ marginRight: '8px', color: '#722ed1' }} />
            <span style={{ fontWeight: 600, fontSize: '16px' }}>Linked Parent Invoice(s)</span>
          </div>}

          <Card 
            style={{ 
              marginBottom: '16px',
              border: '1px solid #e8e3f5',
              backgroundColor: 'white'
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon type="file-text" style={{ fontSize: '16px', color: '#722ed1' }} />
                <span style={{
                  fontWeight: 600,
                  fontSize: '15px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {parent_invoice_details.invoice_number}

                  <Copyable
                    text={parent_invoice_details.invoice_number}
                    color="#1890ff"
                  />
                </span>
              </div>
              {parent_invoice_details.payment_status && getPaymentStatusTag(parent_invoice_details.payment_status)}
            </div>

            <Row gutter={[24, 16]}>
              <Col span={parent_invoice_details.payment_status === 'over paid' ? 6 : 8}>
                <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px' }}>Original Amount</div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#262626' }}>
                  ${parent_invoice_details.original_amount?.toFixed(2) || '0.00'}
                </div>
              </Col>
              <Col span={parent_invoice_details.payment_status === 'over paid' ? 6 : 8}>
                <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px' }}>Amount Paid</div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#228a50' }}>
                  ${parent_invoice_details.amount_paid?.toFixed(2) || '0.00'}
                </div>
              </Col>
              <Col span={parent_invoice_details.payment_status === 'over paid' ? 6 : 8}>
                <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px' }}>Balance</div>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 600, 
                  color: (parent_invoice_details.balance || 0) > 0 ? '#ed5857' : '#228a50' 
                }}>
                  ${parent_invoice_details.balance?.toFixed(2) || '0.00'}
                </div>
              </Col>
              {parent_invoice_details.payment_status === 'over paid' && (
                <Col span={6}>
                  <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px' }}>Over Payment</div>
                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#395484' }}>
                    ${parent_invoice_details.over_payment?.toFixed(2) || '0.00'}
                  </div>
                </Col>
              )}
            </Row>

            {/* Linked Orders */}
            {hasLinkedOrders && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ 
                  color: '#8c8c8c', 
                  fontSize: '12px', 
                  marginBottom: '8px',
                  fontWeight: 500
                }}>
                  Linked Orders (Original HAWBs)
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {linked_orders.map((order, idx) => (
                    <Tag 
                      key={idx}
                      style={{ 
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9',
                        backgroundColor: 'white',
                        fontSize: '12px'
                      }}
                    >
                      {formatLinkedOrder(order)}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {/* Parent Invoice Payments */}
            {parent_invoice_details.transaction_history && parent_invoice_details.transaction_history.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ 
                  color: '#8c8c8c',
                  fontSize: '12px', 
                  marginBottom: '12px',
                  fontWeight: 500
                }}>
                  Transaction History
                </div>
                {renderTransactionHistory(parent_invoice_details.transaction_history)}
              </div>
            )}
          </Card>
        </Card>
      )}

      {/* Payment Status - Debit Invoice */}
      {hasDebitInvoice && (
        <Card 
          style={{ 
            marginBottom: '20px',
            border: '1px solid #f0f0f0'
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon type="file-text" style={{ fontSize: '16px', color: '#722ed1' }} />
              <span style={{ fontWeight: 600, fontSize: '15px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {debit_invoice_details.invoice_number}

                {/* Copy Icon */}
                <Copyable
                  text={debit_invoice_details.invoice_number}
                  color="#1890ff"
                />
              </span>
            </div>
            {debit_invoice_details.payment_status && getPaymentStatusTag(debit_invoice_details.payment_status)}
          </div>

          <Row gutter={[24, 16]}>
            <Col span={debit_invoice_details.payment_status === 'over paid' ? 6 : 8}>
              <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px'}}>
                Invoice Amount
              </div>
              <div style={{ fontSize: '20px', fontWeight: 600, color: '#262626' }}>
                ${debit_invoice_details.original_amount?.toFixed(2) || '0.00'}
              </div>
            </Col>
            <Col span={debit_invoice_details.payment_status === 'over paid' ? 6 : 8}>
              <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px' }}>
                Total Paid
              </div>
              <div style={{ fontSize: '20px', fontWeight: 600, color: '#228a50' }}>
                ${debit_invoice_details.amount_paid?.toFixed(2) || '0.00'}
              </div>
            </Col>
            <Col span={debit_invoice_details.payment_status === 'over paid' ? 6 : 8}>
              <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px'}}>
                Balance Due
              </div>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: 600, 
                color: (debit_invoice_details.balance || 0) > 0 ? '#ed5857' : '#228a50' 
              }}>
                ${debit_invoice_details.balance?.toFixed(2) || '0.00'}
              </div>
            </Col>
            {debit_invoice_details.payment_status === 'over paid' && (
              <Col span={6}>
                <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px'}}>
                  Over Payment
                </div>
                <div style={{ fontSize: '20px', fontWeight: 600, color: '#395484' }}>
                  ${debit_invoice_details.over_payment?.toFixed(2) || '0.00'}
                </div>
              </Col>
            )}
          </Row>

          {/* Debit Invoice Transaction History */}
          {debit_invoice_details.transaction_history && debit_invoice_details.transaction_history.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ 
                color: '#8c8c8c',
                fontSize: '12px',
                marginBottom: '12px',
                fontWeight: 500,
                borderTop: '1px solid #f0f0f0',
                paddingTop: '16px'
              }}>
                Transaction History
              </div>
              {renderTransactionHistory(debit_invoice_details.transaction_history)}
            </div>
          )}
        </Card>
      )}

      {!hasParentInvoice && !hasDebitInvoice && (
        <Alert
          message="No Invoice Data"
          description="No parent or debit invoice details available"
          type="info"
          showIcon
          style={{ margin: '20px 0' }}
        />
      )}
    </div>
  );
};

export default InvoicePayments;